import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { EngagementDetail, SocialAccountStatus } from '@anytag/graphql';
import { Button, Drawer, Form, Input, Label } from '@anytag/shared-atoms';
import { useCopy } from '@anytag/shared-hooks';
import { TextareaField, FileUploadField } from '@anytag/shared-molecules';
import { cn } from '@anytag/shared-utils';
import { EngagementPostFormKeys, EngagementPostFormValues } from '../types';
import { useEngagementPostFormState } from './hooks';
import { FooterButtons, InfluencerAccountFields } from './shared';

interface TTCMTemplateProps {
  onClose: () => void;
  onSubmit?: () => void;
  engagementId: EngagementDetail['id'];
  isTtcmCampaign: EngagementDetail['isTtcmCampaign'];
  initialValues: EngagementPostFormValues;
}
export const TTCMTemplate = ({ onClose, onSubmit, engagementId, isTtcmCampaign, initialValues }: TTCMTemplateProps) => {
  const { t } = useTranslation();
  const { copyToClipboard } = useCopy();
  const { isAdminStaff } = useUserRoles();
  const { selectedSocialAccount } = useEngagementPostFormState({ engagementId });

  const inviteLink = selectedSocialAccount?.ttcmCampaignInviteLink || '';
  const isTiktokAccountNotSignedUp = selectedSocialAccount?.status === SocialAccountStatus.SCRAPING;
  const hasUploadFields =
    isAdminStaff &&
    !!initialValues.id &&
    ((!initialValues.materialsUrl.length && !initialValues.content) || isTiktokAccountNotSignedUp);

  return (
    <>
      <Drawer.Body>
        <Form.SectionTitle index={1} label={t('Video Information')} />

        <InfluencerAccountFields engagementId={engagementId} isTtcmCampaign={isTtcmCampaign} />

        <Form.Fields className="mb-4 items-end" itemsPerRow={2}>
          <Form.Field>
            <Label title={t('TextForm.Invitation link')} />
            <Input placeholder="" value={inviteLink} disabled />
          </Form.Field>

          {inviteLink ? (
            <Form.Field className="pl-0!">
              <Button variant="outline" title={t('Button.Copy')} onClick={() => copyToClipboard(inviteLink)} />
            </Form.Field>
          ) : null}
        </Form.Fields>

        <h3 className={styles.subHeader}>{t('Step1_AddTikTokVideoTitle')}</h3>
        <p className={styles.stepDescription}>{t('Step1_AddTikTokVideoDescription')}</p>
        <br />
        <h3 className={styles.subHeader}>{t('Step2_AddTikTokVideoTitle')}</h3>
        <p className={styles.stepDescription}>{t('Step2_AddTikTokVideoDescription')}</p>

        {hasUploadFields ? (
          <>
            <br />
            <h3 className={styles.subHeader}>{t('Step3_AddTikTokVideoTitle')}</h3>

            {isTiktokAccountNotSignedUp ? (
              <Label className="mt-4 text-red-70" title={t('TtcmPostThumbnailsNote')} />
            ) : null}

            <TextareaField<EngagementPostFormKeys>
              name="content"
              title={t('TextForm.Contents')}
              help={t('Tooltip.Post Content Help')}
              placeholder={t<string>('TextForm.Contents Placeholder')}
            />

            <FileUploadField<EngagementPostFormKeys>
              target="post"
              itemsPerRow={3}
              name="materialsUrl"
              title={t('Video thumbnail')}
              help={t('Tooltip.Files Help')}
              multiple
            />
          </>
        ) : null}
      </Drawer.Body>
      <Drawer.Footer>{hasUploadFields ? <FooterButtons onClose={onClose} onSubmit={onSubmit} /> : null}</Drawer.Footer>
    </>
  );
};

const styles = {
  subHeader: cn('text-base font-semibold'),
  stepDescription: cn('mt-2 text-base font-normal')
};
